// ─── MERCADO PAGO SERVICE ──────────────────────────────────────────────────────
// Este arquivo centraliza toda a integração com Mercado Pago.
//
// CONFIGURAÇÃO:
//   1. Crie uma conta em https://www.mercadopago.com.br/developers
//   2. No painel, vá em "Suas integrações" → "Credenciais"
//   3. Copie sua PUBLIC_KEY e coloque em REACT_APP_MP_PUBLIC_KEY no .env
//   4. No seu BACKEND (Node/Express), use a ACCESS_TOKEN para criar preferências
//
// FLUXO DE PAGAMENTO:
//   Frontend → chama createPreference() no backend → recebe preference_id
//   Frontend → abre Checkout Pro do Mercado Pago com esse preference_id
//   Mercado Pago → redireciona para success_url / failure_url / pending_url
//   Backend → recebe webhook com confirmação do pagamento
// ──────────────────────────────────────────────────────────────────────────────

export const MP_PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY || "TEST-sua-public-key-aqui";

export const PLANS = {
  basico: {
    id: "basico",
    name: "Básico",
    price: 290,
    priceLabel: "R$ 290",
    period: "/mês",
    description: "Para empresas que querem começar a cuidar da sua reputação.",
    features: [
      "Perfil verificado",
      "Responder avaliações",
      "2 vagas publicadas/mês",
      "Painel básico de métricas",
    ],
    notIncluded: [
      "Vagas em destaque",
      "Selo Empresa Inclusiva",
      "Relatório DEI",
    ],
    mpItemId: "wm_plan_basico",
  },
  pro: {
    id: "pro",
    name: "Pro",
    price: 690,
    priceLabel: "R$ 690",
    period: "/mês",
    description: "Para empresas comprometidas com diversidade e atração de talentos.",
    features: [
      "Tudo do Básico",
      "Vagas em destaque",
      "Analytics completo de perfil",
      "Vagas ilimitadas",
      "Selo 'Empresa Inclusiva'",
      "Suporte prioritário",
    ],
    notIncluded: [
      "Relatório DEI mensal",
      "Gestora de conta dedicada",
    ],
    mpItemId: "wm_plan_pro",
    popular: true,
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    price: null,
    priceLabel: "Sob consulta",
    period: "",
    description: "Para grandes empresas com múltiplas unidades e metas de DEI.",
    features: [
      "Tudo do Pro",
      "Relatório DEI mensal",
      "Gestora de conta dedicada",
      "Integração com sistemas de RH",
      "SLA garantido",
      "Treinamento da equipe",
    ],
    notIncluded: [],
    mpItemId: "wm_plan_enterprise",
  },
};

// Carrega o SDK do Mercado Pago dinamicamente
export function loadMercadoPagoSDK() {
  return new Promise((resolve) => {
    if (window.MercadoPago) return resolve(window.MercadoPago);
    const script = document.createElement("script");
    script.src = "https://sdk.mercadopago.com/js/v2";
    script.onload = () => resolve(window.MercadoPago);
    document.head.appendChild(script);
  });
}

// Cria preferência de pagamento via seu backend
// SEU BACKEND deve ter uma rota POST /api/payments/create-preference
export async function createPaymentPreference(plan, companyData) {
  const response = await fetch("/api/payments/create-preference", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan_id: plan.id,
      company_name: companyData.name,
      company_email: companyData.email,
      items: [{
        id: plan.mpItemId,
        title: `WoMom — Plano ${plan.name}`,
        quantity: 1,
        currency_id: "BRL",
        unit_price: plan.price,
      }],
      back_urls: {
        success: `${window.location.origin}/pagamento/sucesso`,
        failure: `${window.location.origin}/pagamento/falha`,
        pending: `${window.location.origin}/pagamento/pendente`,
      },
      auto_return: "approved",
      // Para assinaturas recorrentes, use preapproval_plan no backend
    }),
  });
  if (!response.ok) throw new Error("Erro ao criar preferência de pagamento");
  return response.json(); // { preference_id, init_point }
}

// Abre o Checkout Pro do Mercado Pago
export async function openMercadoPagoCheckout(preferenceId) {
  const MP = await loadMercadoPagoSDK();
  const mp = new MP(MP_PUBLIC_KEY);
  const checkout = mp.checkout({
    preference: { id: preferenceId },
    render: { container: "#mp-checkout-btn", label: "Assinar plano" },
  });
  checkout.open();
}
